// Phần dính trình duyệt của tracker (xem tracker.ts). Gửi về /api/e2e/hae-track — backend e2e
// xác thực phiên, gắn actor, rồi chuyển tiếp sang HAE. Trình duyệt KHÔNG giữ service key.
import {
  CHECKPOINT_MS,
  FLUSH_EVERY_MS,
  IDLE_MS,
  createTracker,
  externalLinkName,
  routeTemplate,
  viewContextFromRoute,
  type TrackEvent,
} from "./tracker"

const ENDPOINT = "/api/e2e/hae-track"

function postEvents(events: TrackEvent[], { beacon = false }: { beacon?: boolean } = {}) {
  try {
    const body = JSON.stringify({ events })
    if (beacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      // sendBeacon = đường duy nhất sống sót qua lúc đóng tab.
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }))
      return
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "include",
    }).catch(() => {})
  } catch {
    /* đo hỏng thì thôi */
  }
}

function newSessionId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
  }
}

type Tracker = ReturnType<typeof createTracker>
let tracker: Tracker | null = null
let stop: (() => void) | null = null
let meId: string | null = null

export function startTracking(): Tracker | null {
  if (tracker || typeof window === "undefined" || typeof document === "undefined") return tracker
  const t = createTracker({
    send: postEvents,
    sessionId: newSessionId(),
    startRunning: document.visibilityState !== "hidden",
  })
  tracker = t

  let idleTimer: ReturnType<typeof setTimeout> | undefined
  const armIdle = () => {
    clearTimeout(idleTimer)
    idleTimer = setTimeout(() => t.pause(), IDLE_MS)
  }
  const onActivity = () => {
    if (document.visibilityState === "hidden") return
    t.resume()
    armIdle()
  }
  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      t.pause()
      t.checkpoint({ minWallMs: 0 })
      t.flush()
    } else onActivity()
  }
  const onLeave = () => {
    t.closeAll()
    t.flush({ beacon: true })
  }
  // [data-track] gần nhất, không có thì link <a> ra ngoài → 'link:<host>'. Capture để nút tự
  // stopPropagation vẫn được đếm.
  const onClick = (e: MouseEvent) => {
    try {
      const el = (e.target as Element | null)?.closest?.("[data-track],a[href]") as HTMLElement | null
      if (!el) return
      if (el.dataset?.track) {
        t.ui(el.dataset.track)
        return
      }
      const name = externalLinkName((el as HTMLAnchorElement).href, location.host)
      if (name) t.ui(name)
    } catch {
      /* đo hỏng thì thôi */
    }
  }

  const ACTIVITY = ["pointerdown", "keydown", "wheel", "touchstart", "focus"] as const
  window.addEventListener("click", onClick, { passive: true, capture: true })
  for (const ev of ACTIVITY) window.addEventListener(ev, onActivity, { passive: true, capture: true })
  document.addEventListener("visibilitychange", onVisibility)
  window.addEventListener("pagehide", onLeave)
  const flushTimer = setInterval(() => {
    t.checkpoint({ minWallMs: CHECKPOINT_MS })
    t.flush()
  }, FLUSH_EVERY_MS)
  armIdle()

  stop = () => {
    for (const ev of ACTIVITY) window.removeEventListener(ev, onActivity, { capture: true })
    window.removeEventListener("click", onClick, { capture: true })
    document.removeEventListener("visibilitychange", onVisibility)
    window.removeEventListener("pagehide", onLeave)
    clearInterval(flushTimer)
    clearTimeout(idleTimer)
    onLeave()
    tracker = null
    stop = null
  }
  return t
}

export function stopTracking() {
  stop?.()
}

/** Gọi tay từ onClick cho thao tác không có data-track. Hỏng thì im. */
export function ui(name: string) {
  try {
    ;(tracker ?? startTracking())?.ui(name)
  } catch {
    /* đo hỏng thì thôi */
  }
}

/** Gọi từ root khi route đổi. routeId = TanStack route id (template). */
export function trackRoute(routeId: string | null, params: Record<string, unknown>, me: string | null) {
  try {
    meId = me
    const t = tracker ?? startTracking()
    if (!t) return
    if (!routeId || routeId === "__root__") return t.setView(null)
    const mobile = !!window.matchMedia?.("(max-width: 820px)").matches
    t.setView(routeTemplate(routeId), viewContextFromRoute(routeTemplate(routeId), params, meId, mobile))
  } catch {
    /* đo hỏng thì thôi */
  }
}
